import { CreatableSelect } from "shared/components";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { getErrorMessage } from "utils/error";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";

interface AmenitiesSelectProps {
  level: "property" | "unit";
  onSelect: (value: number[]) => void;
  selectedAmenities: number[];
  disabled?: boolean;
}

export const AmenitiesSelect = ({
  onSelect,
  selectedAmenities,
  level: _level = "property",
  disabled = false
}: AmenitiesSelectProps) => {
  const queryClient = useQueryClient();

  const { data: amenities, isLoading: isLoadingAmenities } = useQuery<
    { id: number; slug: string; title: string }[]
  >({
    queryKey: ["amenities"],
    queryFn: () =>
      instance
        .get(`portal/v1/partners/amenities`)
        .then(
          (
            res: AxiosResponse<{ id: number; slug: string; title: string }[]>
          ) => {
            return res.data;
          }
        )
  });

  const { mutate: createAmenity, isPending: isCreatingAmenity } = useMutation({
    mutationFn: (amenityTitle: string) =>
      instance
        .post("portal/v1/partners/amenities", {
          title: amenityTitle
        })
        .then(
          (res: AxiosResponse<{ id: number; slug: string; title: string }>) =>
            res.data
        ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["amenities"] });
      onSelect([...selectedAmenities, data.id]);
    },
    onError: (error) => {
      toast.error("Fail to create amenity", getErrorMessage(error));
    }
  });

  return (
    <div className="space-y-3">
      <CreatableSelect
        id="amenities-selector"
        label="Amenities"
        placeholder="Select or type to add new"
        options={
          amenities?.map(({ id, title, slug }) => ({
            value: id,
            label: title,
            id: slug
          })) || []
        }
        isMulti
        loading={isLoadingAmenities || isCreatingAmenity}
        onChange={(e) => onSelect(e.target.value as unknown as number[])}
        value={selectedAmenities}
        isDisabled={disabled}
        formatCreateLabel={(inputValue: string) => `Add "${inputValue}"`}
        handleCreate={createAmenity}
      />
    </div>
  );
};
